import { useId } from "react"

import { CARNET } from "./medidas"

/**
 * El fondo del carnet: olas en capas —vino oscuro, vino medio y un filete dorado—
 * arriba en las dos caras y abajo en el reverso. **El único sitio que dibuja la
 * geometría del carnet.**
 *
 * ⚠ **Es el diseño pedido pasado a la marca: el azul marino es el vino oscuro, el azul
 * es el vino medio y el celeste es el filete dorado.** En la referencia el celeste es
 * una banda ancha; aquí es un filete, porque la versión anterior —píldoras en diagonal
 * en las cuatro esquinas, nombre y cargo en píldora— se veía demasiado cargada, y el
 * oro en banda ancha la volvería a cargar. Un filete dorado siguiendo la ola es además
 * lo que hace la firma corporativa, así que el carnet vuelve a parecerse a los rótulos
 * y a los correos.
 *
 * ⚠ **El filete es la MISMA curva que el borde del vino, bajada milímetro y medio**, no
 * un segundo trazado dibujado a mano: con dos curvas «parecidas» el filete se acerca y
 * se aleja del borde, y eso es lo primero que se ve de cerca. Es la misma decisión que
 * `MarcoImpreso`.
 *
 * ⚠ **El mismo SVG en milímetros**: `viewBox` igual a la medida y
 * `preserveAspectRatio="none"`, así que una unidad del trazado es un milímetro del
 * papel. Y los degradados con `useId()`: el pliego pinta muchas copias en el mismo
 * documento, y un `id` repetido se queda con el primero.
 */

/** Una ola de izquierda a derecha, bajada `d` mm. */
type Ola = (d: number) => string

/**
 * La cabecera del frente: honda a la izquierda, somera a la derecha, y la foto la
 * cruza por el medio. Más baja desde que se quitó el logotipo de arriba: con la foto
 * subida, el nombre quedaba a la altura de la ola por la izquierda — tinta oscura
 * sobre vino.
 */
const FRENTE: Record<"oscura" | "media", Ola> = {
  oscura: (d) =>
    `M0,${25 + d} C10,${25 + d} 17,${21.5 + d} 26,${18 + d} C35,${14.5 + d} 44,${9 + d} 54,${8 + d}`,
  media: (d) =>
    `M0,${30 + d} C11,${30 + d} 18,${26.5 + d} 27,${22.5 + d} C36,${18.5 + d} 45,${14 + d} 54,${13 + d}`,
}

/**
 * El pie del frente: una ola suave de lado a lado, con el vino medio en una franja
 * fina y el oscuro debajo. Encima van el sello del aniversario y el QR.
 */
const PIE_FRENTE: Ola = (d) =>
  `M0,${69.5 + d} C12,${68 + d} 24,${68 + d} 34,${69.5 + d} C42,${70.7 + d} 48,${71 + d} 54,${70 + d}`

/** El grueso de la franja de vino medio del pie del frente, en mm. */
const FRANJA = 2.2

/**
 * La del reverso, al revés: somera a la izquierda, donde empieza el texto, y honda a
 * la derecha, donde va la marca. Más baja que la del frente porque aquí no hay foto
 * que la cruce y sí mucho texto que colocar: con 25,5 mm a la derecha, un contacto de
 * emergencia en dos líneas empujaba la firma contra la ola del pie.
 */
const REVERSO: Record<"oscura" | "media", Ola> = {
  oscura: (d) =>
    `M0,${10 + d} C10,${10 + d} 18,${11.5 + d} 27,${13.8 + d} C36,${16 + d} 45,${17.5 + d} 54,${17.5 + d}`,
  media: (d) =>
    `M0,${14 + d} C10,${14 + d} 18,${15.8 + d} 27,${18.5 + d} C36,${21.2 + d} 45,${23 + d} 54,${23 + d}`,
}

/** El pie del reverso: entra por la izquierda y se apaga hacia la esquina de abajo. */
const PIE: Record<"oscura" | "media", Ola> = {
  media: (d) =>
    `M0,${69 + d} C9,${71.5 + d} 16,${78 + d} 26,${81.5 + d} C36,${85 + d} 46,${85.5 + d} 54,${87 + d}`,
  oscura: (d) =>
    `M0,${75 + d} C8,${77 + d} 14,${81.5 + d} 22,${84 + d} C28,${86 + d} 34,${86.8 + d} 40,${87 + d}`,
}

/** Separación del filete dorado respecto al borde del vino medio, en mm. */
const FILETE = 1.4

export function FondoCarnet({ cara }: { cara: "frente" | "reverso" }) {
  const base = useId().replace(/[^a-zA-Z0-9_-]/g, "")
  const idVino = `cv-${base}`
  const idMedio = `cm-${base}`
  const idOro = `co-${base}`
  const { ancho, alto } = CARNET
  const cabecera = cara === "frente" ? FRENTE : REVERSO
  const vino = `url(#${idVino})`
  const medio = `url(#${idMedio})`
  const oro = `url(#${idOro})`

  return (
    <svg
      viewBox={`0 0 ${ancho} ${alto}`}
      preserveAspectRatio="none"
      aria-hidden={true}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      <defs>
        <linearGradient id={idVino} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="var(--impreso-banda-1)" />
          <stop offset="0.6" stopColor="var(--impreso-banda-2)" />
          <stop offset="1" stopColor="var(--impreso-banda-3)" />
        </linearGradient>
        <linearGradient id={idMedio} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="var(--impreso-panel-3)" />
          <stop offset="1" stopColor="var(--impreso-panel-2)" />
        </linearGradient>
        {/* A lo largo de la tarjeta y con cinco tramos: son los del filete de la firma,
            y es lo que da el brillo metálico en vez de una línea beige. */}
        <linearGradient id={idOro} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={ancho} y2="0">
          <stop offset="0" stopColor="var(--impreso-oro-4)" />
          <stop offset="0.3" stopColor="var(--impreso-oro-2)" />
          <stop offset="0.5" stopColor="var(--impreso-oro-3)" />
          <stop offset="0.7" stopColor="var(--impreso-oro-2)" />
          <stop offset="1" stopColor="var(--impreso-oro-4)" />
        </linearGradient>
      </defs>

      {/* De abajo arriba: el vino medio, su filete y encima el oscuro. */}
      <path d={`${cabecera.media(0)} V0 H0 Z`} fill={medio} />
      <path d={cabecera.media(FILETE)} fill="none" stroke={oro} strokeWidth={0.6} />
      <path d={`${cabecera.oscura(0)} V0 H0 Z`} fill={vino} />

      {cara === "reverso" ? (
        <>
          <path d={`${PIE.media(0)} H0 Z`} fill={medio} />
          <path d={PIE.media(-FILETE)} fill="none" stroke={oro} strokeWidth={0.6} />
          <path d={`${PIE.oscura(0)} H0 Z`} fill={vino} />
        </>
      ) : (
        <>
          <path d={`${PIE_FRENTE(0)} V${alto} H0 Z`} fill={medio} />
          <path d={PIE_FRENTE(-FILETE)} fill="none" stroke={oro} strokeWidth={0.6} />
          <path d={`${PIE_FRENTE(FRANJA)} V${alto} H0 Z`} fill={vino} />
        </>
      )}
    </svg>
  )
}
